import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { AdSlot } from "@/components/layout/AdSlot";
import { SearchBar } from "@/components/layout/SearchBar";
import { ToolCard } from "@/components/layout/ToolCard";
import { tools, categories, distinctTools, distinctToolsCount } from "@/lib/tools-registry";
import blogPostsData from "@/data/blog-posts.json";
import { Hero3DWrapper } from "@/components/3d/Hero3DWrapper";
import {
  Sparkles, ShieldCheck, Zap, BadgeCheck, MapPin, EyeOff,
  Search as SearchIcon, MousePointerClick, CheckCircle2, Star,
  Calculator, Percent, Coins, Landmark, GraduationCap, Flame, ArrowRight,
  Shield, Cpu, BookOpen,
} from "lucide-react";

const FAQS = [
  { q: "Is PakDigitalz.com really free?", a: `Yes. All ${distinctToolsCount} tools are 100% free, no signup, no limits. We never charge for any tool.` },
  { q: "Are the calculations accurate for Pakistan?", a: "Yes. Our tax, electricity and Zakat tools use Pakistan-specific formulas (Tax Year 2027 (FY 2026-27) for salaried individuals, WAPDA slabs, 2.5% Zakat). We update rates when government policies change." },
  { q: "Do you store my data?", a: "No. Every tool runs entirely in your browser. We never send your inputs to a server. Your salary, tax, CGPA, and all other data stays on your device." },
  { q: "Which currencies does the converter support?", a: "PKR to USD, SAR, AED, GBP, EUR, INR, CAD, AUD and more using live exchange rates." },
  { q: "How is income tax calculated?", a: "We use the FBR Tax Year 2027 (FY 2026-27) salaried slabs (salaried individuals; non-salaried slabs differ): 0% up to 600k, then progressive rates on higher brackets. Both yearly and monthly calculations are shown." },
  { q: "Can I use these tools on mobile?", a: "Yes. PakDigitalz.com is mobile-first and works on any phone, tablet or desktop browser. No app download needed." },
  { q: "Are prayer times accurate?", a: "Prayer times come from the AlAdhan API using the Karachi calculation method (University of Islamic Sciences) by default. You can search any city in Pakistan." },
  { q: "Can I suggest a new tool?", a: "Absolutely — visit our Contact page and send us your idea. We regularly add new tools based on user requests." },
  { q: "How is PakDigitalz different from other calculator websites?", a: "Unlike generic calculator sites, PakDigitalz uses Pakistan-specific formulas (FBR tax slabs, WAPDA tariffs, PTA duties). All tools are 100% free, require no signup, store no data, and work offline after first load." },
  { q: "Which tax slabs does the Income Tax Calculator use?", a: "The calculator uses FBR Tax Year 2027 (FY 2026-27) salaried person tax slabs (salaried individuals; non-salaried slabs differ): 0% up to PKR 600,000, then progressive rates on higher income brackets." },
  { q: "How do I calculate my electricity bill in Pakistan?", a: "Use our Electricity Bill Calculator — enter your units consumed, select your distribution company (WAPDA, K-Electric, etc.), and get an instant estimate based on current domestic tariff slabs." },
  { q: "Is PakDigitalz safe to use for financial calculations?", a: "Yes. All calculations run locally in your browser. We never access, store, or transmit your financial data. The site uses HTTPS encryption and has no login system." },
  { q: "What is CGPA and how is it calculated?", a: "CGPA (Cumulative Grade Point Average) is calculated on a 4.0 scale used by Pakistani and international universities. Enter your course grades and credit hours — our tool calculates your semester GPA and cumulative CGPA instantly." },
  { q: "Do I need to install an app to use PakDigitalz?", a: "No. PakDigitalz.com works directly in your browser — Chrome, Safari, Firefox, or any other. No download, no installation, no storage space needed." },
];

const popular = ["roth-ira-calculator", "us-paycheck-calculator", "hysa-calculator", "mortgage-calculator", "one-rep-max-calculator", "mortgage-payoff-calculator", "refinance-calculator", "calorie-calculator", "mortgage-affordability-calculator", "heloc-calculator", "solar-roi-calculator", "life-insurance-calculator", "sleep-calculator", "tip-calculator", "random-number-generator", "unit-converter", "meta-tag-generator"];

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: `PakDigitalz: ${distinctToolsCount} Free Online Calculators & Tools` },
      { name: "description", content: "Free online calculators: Income Tax, GST, CGPA, Zakat, Prayer Times, Electricity Bill, Loan EMI, 401k & Retirement tools. Fast, 100% private & mobile-friendly." },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: `PakDigitalz: ${distinctToolsCount} Free Online Calculators & Tools` },
      { property: "og:description", content: "All-in-one digital toolkit: tax, finance, student & daily calculators updated for 2026-2027." },
      { property: "og:image", content: "https://pakdigitalz.com/og-image.png" },
      { property: "og:url", content: "https://pakdigitalz.com" },
    ],
    links: [
      { rel: "api-catalog", href: "/.well-known/api-catalog" },
      { rel: "service-doc", href: "/about" },
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
});

const WORDS = ["Finance & Tax", "Students & Study", "Everyday Math", "Daily Work"];

const WHY = [
  { icon: Zap, title: "Lightning Fast", body: "Runs locally in your browser with instant client-side execution." },
  { icon: BadgeCheck, title: "100% Free Forever", body: "Every tool, every feature. No accounts, no paywalls, no limits." },
  { icon: MapPin, title: "Pakistan-Specific Slabs", body: "FBR tax slabs Tax Year 2027 (FY 2026-27), WAPDA tariffs & local prayer methods." },
  { icon: EyeOff, title: "100% Private & Secure", body: "Zero server storage. Your numbers stay strictly on your device." },
];

const STEPS = [
  { icon: SearchIcon, title: "Search a Tool", body: `Find any of ${distinctToolsCount} calculators from the instant search bar.` },
  { icon: MousePointerClick, title: "Enter Values", body: "Type your numbers — no account or signup required." },
  { icon: CheckCircle2, title: "Get Instant Result", body: "See precise, verified calculations generated in real-time." },
];

function Index() {
  const [wordIdx, setWordIdx] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [recentTools, setRecentTools] = useState<typeof tools>([]);
  const [showAllFinance, setShowAllFinance] = useState(false);
  const [showAllStudents, setShowAllStudents] = useState(false);
  const [showAllDaily, setShowAllDaily] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const interval = setInterval(() => {
      setWordIdx((prev) => (prev + 1) % WORDS.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("pakdigitalz_recent_tools");
      if (stored) {
        const slugs: string[] = JSON.parse(stored);
        setRecentTools(
          slugs
            .map((s) => tools.find((t) => t.slug === s))
            .filter((t): t is NonNullable<typeof t> => t != null)
            .slice(0, 4)
        );
      }
    } catch {}
  }, []);

  const finance = distinctTools.filter((t) => t.category === "finance");
  const students = distinctTools.filter((t) => t.category === "students");
  const daily = distinctTools.filter((t) => t.category === "daily");
  const popularTools = popular.slice(0, 8).map((s) => tools.find((t) => t.slug === s)).filter((t): t is NonNullable<typeof t> => t != null);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border hero-gradient">
        <Hero3DWrapper />
        <div className="absolute inset-0 grid-pattern pointer-events-none animate-grid" />

        <div className="hero-blob hero-blob-1 -top-16 -left-16 pointer-events-none" />
        <div className="hero-blob hero-blob-2 bottom-12 -right-12 pointer-events-none" />

        <Calculator className="hero-floating-icon h-10 w-10 sm:h-16 sm:w-16 top-10 left-4 md:left-24" style={{ animationDelay: "0s" }} />
        <Percent className="hero-floating-icon h-9 w-9 sm:h-14 sm:w-14 top-20 right-4 md:right-28" style={{ animationDelay: "1.5s" }} />
        <Coins className="hero-floating-icon h-10 w-10 sm:h-16 sm:w-16 bottom-16 left-6 md:left-32" style={{ animationDelay: "3.5s" }} />
        <Star className="hero-floating-icon h-8 w-8 sm:h-12 sm:w-12 bottom-24 right-6 md:right-40" style={{ animationDelay: "5s" }} />
        <Sparkles className="hero-floating-icon h-9 w-9 sm:h-14 sm:w-14 top-1/2 left-4 md:left-16" style={{ animationDelay: "2.5s" }} />
        <ShieldCheck className="hero-floating-icon h-9 w-9 sm:h-14 sm:w-14 top-1/3 right-6 md:right-36" style={{ animationDelay: "4s" }} />

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 md:py-24 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border px-3.5 py-1 text-xs font-bold text-muted-foreground mb-6 shadow-sm animate-scale-in">
            <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" /> Quick &amp; Accurate · 100% Free Suite
          </span>
          
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight text-foreground leading-[1.15] md:leading-[1.05] animate-fade-in-up anim-delay-100">
            <span className="font-black text-foreground drop-shadow-xs">{distinctToolsCount} Free Online Tools</span><br className="hidden md:block" />
            <span className="text-primary block mt-1.5 font-extrabold relative">
              <span className="h-[1.2em] overflow-hidden block relative">
                <span key={isMounted ? wordIdx : "initial"} className={isMounted ? "word-animate absolute left-0 right-0" : "absolute left-0 right-0"}>
                  for {isMounted ? WORDS[wordIdx] : WORDS[0]}
                </span>
              </span>
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in-up anim-delay-200 leading-relaxed">
            Tax, CGPA, Zakat, Prayer Times, Electricity Bill, Loan EMI and more — all in one place, mobile-friendly and lightning fast.
          </p>

          <div className="mt-8 flex justify-center animate-fade-in-up anim-delay-300">
            <div className="w-full max-w-2xl">
              <SearchBar large />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3 animate-fade-in-up anim-delay-500">
            {[
              { Icon: Zap, label: "No Signup" },
              { Icon: ShieldCheck, label: "100% Free" },
              { Icon: EyeOff, label: "100% Client-Side Privacy 🔒" },
            ].map(({ Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 rounded-full bg-card border border-border/80 px-4 py-2 text-sm font-semibold text-foreground shadow-xs hover:border-primary/50 transition duration-200">
                <Icon className="h-4 w-4 text-primary" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <AdSlot variant="header" />

      {/* Stats Bar */}
      <section className="border-y border-border bg-primary-soft/40">
        <div className="mx-auto max-w-7xl px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { k: `${distinctToolsCount}`, v: "Online Calculators" },
            { k: "100%", v: "Free Forever" },
            { k: "Private", v: "Client-Side Processing" },
            { k: "0", v: "Signups Required" },
          ].map((s) => (
            <div key={s.v} className="p-2">
              <div className="text-2xl md:text-3xl font-black text-foreground tracking-tight">{s.k}</div>
              <div className="text-xs md:text-sm font-medium text-muted-foreground">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Dedicated Trust Signals Strip */}
      <section className="mx-auto max-w-7xl px-4 pt-12">
        <div className="rounded-3xl border border-border/80 bg-gradient-to-r from-card via-primary-soft/20 to-card p-6 md:p-8 shadow-xs backdrop-blur-xs">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-xs font-extrabold text-primary mb-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified Security &amp; Privacy Standards
            </span>
            <h2 className="text-xl md:text-2xl font-black text-foreground tracking-tight">
              Why Users Trust PakDigitalz.com
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-xl mx-auto mt-1">
              Built with client-side execution, instant responsiveness, and zero data tracking.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="flex items-start gap-3.5 rounded-2xl bg-card/80 border border-border/60 p-4 shadow-2xs hover:border-primary/40 transition">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <EyeOff className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-foreground text-sm">100% Data Privacy</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">Calculations execute inside your browser. No server storage.</p>
              </div>
            </div>

            <div className="flex items-start gap-3.5 rounded-2xl bg-card/80 border border-border/60 p-4 shadow-2xs hover:border-primary/40 transition">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                <Zap className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-foreground text-sm">Zero Signup Friction</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">Instant 1-click access to all tools without registrations or ads overload.</p>
              </div>
            </div>

            <div className="flex items-start gap-3.5 rounded-2xl bg-card/80 border border-border/60 p-4 shadow-2xs hover:border-primary/40 transition">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
                <BadgeCheck className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-foreground text-sm">Official Formulas</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">Updated FBR Tax Year 2027 (FY 2026-27) tax slabs (salaried individuals; non-salaried slabs differ), WAPDA rates &amp; financial math.</p>
              </div>
            </div>

            <div className="flex items-start gap-3.5 rounded-2xl bg-card/80 border border-border/60 p-4 shadow-2xs hover:border-primary/40 transition">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/15 text-purple-600 dark:text-purple-400">
                <Cpu className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-foreground text-sm">Instant Browser Math</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">Fast response speeds running directly inside your browser on any device.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Grid Content Area */}
      <main className="mx-auto w-full max-w-7xl px-4 py-12 space-y-16 md:space-y-24">
        {recentTools.length > 0 && (
          <section className="animate-fade-in-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="h-3 w-3 rounded-full bg-primary animate-pulse" />
              <h2 className="text-2xl font-black text-foreground tracking-tight">Recently Used Tools</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger-children">
              {recentTools.map((t) => <ToolCard key={t.slug} tool={t} />)}
            </div>
          </section>
        )}

        {/* Most Popular Section with High-Contrast Background Block */}
        <section className="rounded-3xl border border-border/80 bg-muted/40 p-6 md:p-10 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-8 pb-4 border-b border-border/60">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">
                <Flame className="h-4 w-4 fill-amber-500 text-amber-500" /> Top Performing Suite
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">Most Popular Tools</h2>
            </div>
            <span className="text-xs font-bold text-muted-foreground bg-card border border-border/80 px-3 py-1.5 rounded-full self-start sm:self-auto shadow-2xs">
              {popularTools.length} Featured Tools
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger-children">
            {popularTools.map((t) => <ToolCard key={t.slug} tool={t} badge="Popular" />)}
          </div>
        </section>

        {/* Category: Finance & Business */}
        <section id="finance" className="rounded-3xl border border-border/80 bg-card/60 p-6 md:p-10 shadow-xs backdrop-blur-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-8 pb-4 border-b border-border/60">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">
                <Landmark className="h-4 w-4" /> Finance &amp; Tax Suite
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">{categories.finance.label}</h2>
              <p className="text-muted-foreground mt-1 text-sm">{categories.finance.blurb}</p>
            </div>
            <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full self-start sm:self-auto">
              {finance.length} Calculators
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger-children">
            {(showAllFinance ? finance : finance.slice(0, 6)).map((t) => <ToolCard key={t.slug} tool={t} />)}
          </div>
          {!showAllFinance && finance.length > 6 && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAllFinance(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition"
              >
                View All {finance.length} Finance Calculators ↓
              </button>
            </div>
          )}
        </section>

        {/* Category: Student & Education */}
        <section id="students" className="rounded-3xl border border-border/80 bg-muted/40 p-6 md:p-10 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-8 pb-4 border-b border-border/60">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1">
                <GraduationCap className="h-4 w-4" /> Academic &amp; Student Tools
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">{categories.students.label}</h2>
              <p className="text-muted-foreground mt-1 text-sm">{categories.students.blurb}</p>
            </div>
            <span className="text-xs font-bold text-indigo-700 dark:text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-full self-start sm:self-auto">
              {students.length} Utilities
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger-children">
            {(showAllStudents ? students : students.slice(0, 6)).map((t) => <ToolCard key={t.slug} tool={t} />)}
          </div>
          {!showAllStudents && students.length > 6 && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAllStudents(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 transition"
              >
                View All {students.length} Academic Tools ↓
              </button>
            </div>
          )}
        </section>

        {/* Category: Daily Use */}
        <section id="daily" className="rounded-3xl border border-border/80 bg-card/60 p-6 md:p-10 shadow-xs backdrop-blur-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-8 pb-4 border-b border-border/60">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">
                <Calculator className="h-4 w-4" /> Everyday Utilities
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">{categories.daily.label}</h2>
              <p className="text-muted-foreground mt-1 text-sm">{categories.daily.blurb}</p>
            </div>
            <span className="text-xs font-bold text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-full self-start sm:self-auto">
              {daily.length} Tools
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger-children">
            {(showAllDaily ? daily : daily.slice(0, 6)).map((t) => <ToolCard key={t.slug} tool={t} />)}
          </div>
          {!showAllDaily && daily.length > 6 && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAllDaily(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-xs font-bold text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition"
              >
                View All {daily.length} Everyday Tools ↓
              </button>
            </div>
          )}
        </section>

        {/* Why PakDigitalz */}
        <section className="w-full overflow-hidden rounded-3xl border border-border/80 bg-card p-6 md:p-10 shadow-xs">
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="text-xs font-bold text-primary uppercase tracking-wider">Features &amp; Benefits</span>
            <h2 className="text-2xl md:text-3xl font-black text-foreground tracking-tight mt-1">Why PakDigitalz?</h2>
            <p className="text-muted-foreground text-sm mt-1">Quick, private, and simple tools designed for everyday calculations.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            {WHY.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-border/80 bg-muted/30 p-6 shadow-2xs hover:border-primary/50 transition-all flex flex-col justify-between h-full">
                <div>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary mb-4 shadow-2xs" aria-hidden="true">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-extrabold text-foreground text-base mb-1.5">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="rounded-3xl border border-border/80 bg-primary-soft/30 p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-black text-foreground text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md" aria-hidden="true">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-xs font-extrabold text-primary uppercase tracking-wider mb-1">STEP {i + 1}</div>
                <h3 className="font-extrabold text-foreground text-base mb-1.5">{title}</h3>
                <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>



        {/* Latest Blog Guides — Using <Link> for TanStack Router inlink equity */}
        <section className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-wider">Helpful Resources</span>
              <h2 className="text-2xl font-black text-foreground tracking-tight">Latest Guides &amp; Articles</h2>
            </div>
            <Link to="/blog" className="text-xs font-bold text-primary hover:underline flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            <Link to="/blog/$slug" params={{ slug: "roth-ira-2026-guide" }} className="flex flex-col justify-between p-5 rounded-2xl border border-border/80 bg-card hover:border-primary/60 transition-colors shadow-2xs">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-md inline-block mb-2">Finance Guide</span>
                <h3 className="font-bold text-foreground text-sm leading-snug mb-1">Roth IRA 2026 Contribution Limits &amp; Rules</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Complete guide to Roth IRA contribution rules for 2026.</p>
              </div>
              <span className="text-xs font-bold text-primary mt-4 flex items-center gap-1">Read Guide →</span>
            </Link>
            <Link to="/blog/$slug" params={{ slug: "roth-vs-traditional-ira" }} className="flex flex-col justify-between p-5 rounded-2xl border border-border/80 bg-card hover:border-primary/60 transition-colors shadow-2xs">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-md inline-block mb-2">IRA Comparison</span>
                <h3 className="font-bold text-foreground text-sm leading-snug mb-1">Roth vs Traditional IRA — Which Is Better?</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Key differences to help you choose the right retirement account.</p>
              </div>
              <span className="text-xs font-bold text-primary mt-4 flex items-center gap-1">Read Guide →</span>
            </Link>
            <Link to="/blog/$slug" params={{ slug: "backdoor-roth-ira-guide" }} className="flex flex-col justify-between p-5 rounded-2xl border border-border/80 bg-card hover:border-primary/60 transition-colors shadow-2xs">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-purple-600 dark:text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded-md inline-block mb-2">Advanced Strategy</span>
                <h3 className="font-bold text-foreground text-sm leading-snug mb-1">Backdoor Roth IRA — Step-by-Step Guide</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">How high-income earners can still contribute to a Roth IRA.</p>
              </div>
              <span className="text-xs font-bold text-primary mt-4 flex items-center gap-1">Read Guide →</span>
            </Link>
          </div>

          {/* Static all-blog-posts index — SSR-visible compact link list for Googlebot inlink discovery */}
          <div className="mt-6 rounded-2xl border border-border/60 bg-card/60 p-6">
            <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" /> All Published Guides
            </h3>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {(blogPostsData as { slug: string; title: string }[]).map(post => (
                <li key={post.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 flex items-center gap-1.5"
                  >
                    <ArrowRight className="h-3 w-3 text-primary/60 shrink-0" />
                    <span className="truncate">{post.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* All Free Online Tools Overview */}
        <section className="max-w-5xl mx-auto space-y-8 rounded-3xl border border-border/80 bg-card p-6 md:p-10 shadow-xs">
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">All Free Online Tools &amp; Calculators</h2>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Explore {distinctToolsCount} free calculators across finance, tax, health, education, developer utilities, and daily calculations. 100% free, private, and browser-native.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-border/80 bg-muted/30 p-6 space-y-4 shadow-2xs">
              <div className="flex items-center gap-2 pb-3 border-b border-border/60 text-primary font-black text-sm">
                <Landmark className="h-4 w-4" aria-hidden="true" />
                <span>Finance &amp; Tax ({distinctTools.filter(t => t.category === 'finance').length})</span>
              </div>
              <ul className="space-y-2 text-xs" role="list">
                {distinctTools.filter(t => t.category === 'finance').map(t => (
                  <li key={t.slug}>
                    <a href={`/${t.slug}`} className="flex items-center justify-between text-muted-foreground hover:text-primary transition-colors py-0.5 font-medium">
                      <span className="truncate">{t.name.split('|')[0].trim()}</span>
                      <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-2" aria-hidden="true">→</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-border/80 bg-muted/30 p-6 space-y-4 shadow-2xs">
              <div className="flex items-center gap-2 pb-3 border-b border-border/60 text-primary font-black text-sm">
                <GraduationCap className="h-4 w-4" aria-hidden="true" />
                <span>Student Utilities ({distinctTools.filter(t => t.category === 'students').length})</span>
              </div>
              <ul className="space-y-2 text-xs" role="list">
                {distinctTools.filter(t => t.category === 'students').map(t => (
                  <li key={t.slug}>
                    <a href={`/${t.slug}`} className="flex items-center justify-between text-muted-foreground hover:text-primary transition-colors py-0.5 font-medium">
                      <span className="truncate">{t.name.split('|')[0].trim()}</span>
                      <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-2" aria-hidden="true">→</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-border/80 bg-muted/30 p-6 space-y-4 shadow-2xs">
              <div className="flex items-center gap-2 pb-3 border-b border-border/60 text-primary font-black text-sm">
                <Calculator className="h-4 w-4" aria-hidden="true" />
                <span>Daily &amp; Health ({distinctTools.filter(t => t.category === 'daily').length})</span>
              </div>
              <ul className="space-y-2 text-xs" role="list">
                {distinctTools.filter(t => t.category === 'daily').map(t => (
                  <li key={t.slug}>
                    <a href={`/${t.slug}`} className="flex items-center justify-between text-muted-foreground hover:text-primary transition-colors py-0.5 font-medium">
                      <span className="truncate">{t.name.split('|')[0].trim()}</span>
                      <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-2" aria-hidden="true">→</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-black text-foreground mb-6 text-center tracking-tight">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-border/80 bg-card p-5 transition-all shadow-2xs">
                <summary className="cursor-pointer font-bold text-foreground text-sm flex items-center justify-between">
                  <span>{f.q}</span>
                  <span className="text-primary text-base transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground border-t border-border/40 pt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
